/**
 * CallControls - Call Action Buttons
 */

import React from 'react';
import { CallState } from '../../hooks/useSIP';

function CallControls({
  callState,
  isMuted,
  isLocalHold,
  hasConsultSession,
  transferState,
  dialNumber,
  onDial,
  onAnswer,
  onHangup,
  onToggleMute,
  onHold,
  onUnhold,
  onTransferClick,
  onConferenceClick,
  onCompleteTransfer,
  onCancelTransfer,
  onMergeConference,
  onReleaseConference,
  onEndAll,
  conferenceMerged,
  onToggleDialpad,
  showDialpad,
  // MC-1 (2026-07-02): multi-call New Call button.
  onNewCall,
  canStartNewCall,
  // 2026-07-26: Add Call mode (mirrors Transfer > Conference, no dialog).
  addCallMode,
  onCancelAddCall,
  // 2026-08-10: N-party expandable conference active. When true, the
  // conference is shown as ONE line in the ConferenceBar (with its own
  // Release me / End all / Add Call), and CallControls should render the
  // NORMAL full controls (mute / hold / dialpad / Add Call / End) exactly
  // like a single call — NOT the old takeover conference panel (Option A,
  // Fernando 2026-08-10). Suppresses the legacy CONFERENCING panel below.
  nPartyConf,
}) {
  // ═══════════════════════════════════════════════════════════════
  // IDLE STATE - Dial controls
  // ═══════════════════════════════════════════════════════════════
  
  if (callState === CallState.IDLE) {
    return (
      <div className="cx-controls">
        <div className="cx-controls__row">
          <button
            className="cx-controls__btn cx-controls__btn--text cx-controls__btn--call"
            onClick={onDial}
            disabled={!dialNumber}
            title="Dial"
          >
            Call
          </button>

          <button
            className={`cx-controls__btn cx-controls__btn--text ${showDialpad ? 'cx-controls__btn--active' : ''}`}
            onClick={onToggleDialpad}
            title="Toggle Dialpad"
          >
            Dialpad
          </button>
        </div>
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════
  // INCOMING CALL - Answer/Reject
  // ═══════════════════════════════════════════════════════════════
  
  if (callState === CallState.INCOMING) {
    return (
      <div className="cx-controls">
        <div className="cx-controls__row cx-controls__row--incoming">
          <button
            className="cx-controls__btn cx-controls__btn--text cx-controls__btn--answer"
            onClick={onAnswer}
            title="Answer (Enter)"
          >
            Answer
          </button>

          <button
            className="cx-controls__btn cx-controls__btn--text cx-controls__btn--reject"
            onClick={onHangup}
            title="Reject (Esc)"
          >
            Reject
          </button>
        </div>
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════
  // RINGING/CONNECTING - Cancel only
  // ═══════════════════════════════════════════════════════════════
  
  if (callState === CallState.RINGING || callState === CallState.CONNECTING) {
    return (
      <div className="cx-controls">
        <div className="cx-controls__row">
          <button
            className="cx-controls__btn cx-controls__btn--text cx-controls__btn--hangup"
            onClick={onHangup}
            title="Cancel (Esc)"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════
  // TRANSFERRING - Complete/Cancel transfer
  // ═══════════════════════════════════════════════════════════════
  
  if (callState === CallState.TRANSFERRING && transferState?.type === 'attended') {
    return (
      <div className="cx-controls">
        <div className="cx-controls__info">
          Consulting: {transferState.target}
        </div>

        <div className="cx-controls__row">
          {/* 2026-07-15 pm: Mute during consult. onToggleMute already
              dispatches mute/unmute to both session.current and
              consultSession (see useSIP toggleMute) — the UI was just
              missing. */}
          <button
            className={`cx-controls__btn cx-controls__btn--text ${isMuted ? 'cx-controls__btn--active' : ''}`}
            onClick={onToggleMute}
            title={`${isMuted ? 'Unmute' : 'Mute'} (M)`}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </button>

          {transferState.step === 'connected' && (
            <button
              className="cx-controls__btn cx-controls__btn--text cx-controls__btn--success"
              onClick={onCompleteTransfer}
              title="Complete Transfer"
            >
              Xfer
            </button>
          )}

          <button
            className="cx-controls__btn cx-controls__btn--text"
            onClick={onCancelTransfer}
            title="Cancel Transfer"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════
  // CONFERENCING - Merge/Drop controls
  // ═══════════════════════════════════════════════════════════════
  
  // Render the conference panel whenever we're in CONFERENCING state OR
  // already merged. After the merge the old consult session is hung up
  // by the backend (so hasConsultSession briefly drops false), but we
  // still want the Release button visible while the new auto-answered
  // agent leg comes up.
  if (callState === CallState.CONFERENCING && (hasConsultSession || conferenceMerged)
      && !nPartyConf) {
    return (
      <div className="cx-controls">
        <div className="cx-controls__info">
          {conferenceMerged ? '👥 Conference (3-way active)' : 'Conference Call — talking to 3rd party'}
        </div>

        <div className="cx-controls__row">
          <button
            className={`cx-controls__btn cx-controls__btn--text ${isMuted ? 'cx-controls__btn--active' : ''}`}
            onClick={onToggleMute}
            title={`${isMuted ? 'Unmute' : 'Mute'} (M)`}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </button>

          {!conferenceMerged && (
            <button
              className="cx-controls__btn cx-controls__btn--text cx-controls__btn--success"
              onClick={onMergeConference}
              title="Merge the held caller into the call with the 3rd party"
            >
              Merge
            </button>
          )}

          {conferenceMerged && (
            <button
              className="cx-controls__btn cx-controls__btn--text cx-controls__btn--hangup"
              onClick={onReleaseConference}
              title="Leave the conference (caller + 3rd party stay connected)"
            >
              ReleaseMe
            </button>
          )}

          <button
            className="cx-controls__btn cx-controls__btn--text cx-controls__btn--hangup"
            onClick={onEndAll || onHangup}
            title="Hang up everyone in the conference"
          >
            END
          </button>
        </div>
      </div>
    );
  }
  
  // ═══════════════════════════════════════════════════════════════
  // CONNECTED/HOLDING - Full controls
  // ═══════════════════════════════════════════════════════════════
  
  return (
    <div className="cx-controls">
      {/* Add Call mode: prompt agent to dial the 3rd party, then Call.
          This routes through the same conference flow as Transfer > Conf. */}
      {addCallMode && (
        <div className="cx-controls__row">
          <button
            className="cx-controls__btn cx-controls__btn--text cx-controls__btn--answer cx-controls__btn--large"
            onClick={onDial}
            disabled={!dialNumber}
            title="Dial the 3rd party and start a conference"
          >
            Call {dialNumber ? dialNumber : '(enter number)'}
          </button>
          <button
            className="cx-controls__btn cx-controls__btn--text"
            onClick={onCancelAddCall}
            title="Cancel Add Call"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Primary action row — text-label buttons (2026-08-10, per Fernando:
          avoid icon-only ambiguity for agents). */}
      <div className="cx-controls__row">
        <button
          className={`cx-controls__btn cx-controls__btn--text ${isMuted ? 'cx-controls__btn--active' : ''}`}
          onClick={onToggleMute}
          title={`${isMuted ? 'Unmute' : 'Mute'} (M)`}
        >
          {isMuted ? 'Unmute' : 'Mute'}
        </button>

        <button
          className={`cx-controls__btn cx-controls__btn--text ${isLocalHold ? 'cx-controls__btn--active' : ''}`}
          onClick={isLocalHold ? onUnhold : onHold}
          title={`${isLocalHold ? 'Resume' : 'Hold'} (H)`}
        >
          {isLocalHold ? 'Resume' : 'HOLD'}
        </button>

        <button
          className="cx-controls__btn cx-controls__btn--text"
          onClick={onTransferClick}
          title="Transfer (T)"
        >
          Xfer
        </button>

        <button
          className="cx-controls__btn cx-controls__btn--text"
          onClick={onConferenceClick}
          title="Conference"
        >
          CONF
        </button>

        {onNewCall && canStartNewCall && !addCallMode && (
          <button
            className="cx-controls__btn cx-controls__btn--text"
            onClick={onNewCall}
            title="Add a 3rd party to the call (conference)"
          >
            +Call
          </button>
        )}
      </div>

      {/* Hangup row */}
      <div className="cx-controls__row">
        <button
          className="cx-controls__btn cx-controls__btn--text cx-controls__btn--hangup cx-controls__btn--large"
          onClick={onHangup}
          title="End Call (Esc)"
          aria-label="End Call"
        >
          END
        </button>
      </div>
    </div>
  );
}

export default CallControls;
